import React, { useState } from "react";
import { Loader, CalendarDays, Clock, Users, MessageSquare, Minus, Plus, CheckCircle2, ChevronRight } from "lucide-react";
import BackButton from "./components/BackButton";
import PrimaryButton from "./components/PrimaryButton";
import Header from "./components/Header";
import useNav from "./hooks/useNav";
import { useRestaurant } from "./context/RestaurantContext.jsx";
import { useUser } from "./context/UserContext.jsx";
import { toast } from "react-toastify";
import appConfig from "./config";
import { useAuthFetch } from "./hooks/useAuthFetch";

const SectionCard = ({ icon: Icon, title, children }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
        <div className="flex items-center gap-2.5 mb-4">
            <div className="w-7 h-7 rounded-lg bg-brandBlue/10 flex items-center justify-center shrink-0">
                <Icon size={14} className="text-brandBlue" />
            </div>
            <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        </div>
        {children}
    </div>
);

const fieldClass = "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:border-brandBlue focus:bg-white transition-colors disabled:opacity-50";

const ReservationPage = () => {
    const navigate = useNav();
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [peopleCount, setPeopleCount] = useState(2);
    const [comment, setComment] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [reservationId, setReservationId] = useState(null);

    const { restaurant } = useRestaurant();
    const { user } = useUser();
    const { authFetch } = useAuthFetch();

    const preorderEnabled = restaurant?.settings?.preorder_enabled ?? false;

    const today = new Date().toISOString().split("T")[0];

    const workingHoursStart = restaurant?.settings?.working_hours_start ?? '10:00';
    const workingHoursEnd   = restaurant?.settings?.working_hours_end   ?? '23:00';

    const minTime = (() => {
        if (date !== today) return workingHoursStart;
        const now = new Date();
        const h = String(now.getHours()).padStart(2, '0');
        const m = String(now.getMinutes()).padStart(2, '0');
        const nowStr = `${h}:${m}`;
        return nowStr > workingHoursStart ? nowStr : workingHoursStart;
    })();

    const handleReservation = async () => {
        if (!date) { toast.error("Укажите дату"); return; }
        if (!time) { toast.error("Укажите время"); return; }
        if (time < workingHoursStart || time > workingHoursEnd) {
            toast.error(`Ресторан работает с ${workingHoursStart} до ${workingHoursEnd}`);
            return;
        }
        if (date === today && time < minTime) {
            toast.error("Нельзя указать прошедшее время");
            return;
        }

        setIsSubmitting(true);
        try {
            const data = await authFetch(`${appConfig.endpoint}/reservations/create`, {
                method: "POST",
                body: JSON.stringify({
                    date,
                    time,
                    people_count: peopleCount,
                    user_id: user?.id,
                    comment,
                    restaurant_id: restaurant.id,
                }),
            });

            if (data.success) {
                window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
                const minutesUntil = (new Date(`${date}T${time}`).getTime() - Date.now()) / 60000;
                if (preorderEnabled && data.reservation_id && minutesUntil > 30) {
                    setReservationId(data.reservation_id);
                } else {
                    toast.success("Столик успешно забронирован!");
                    navigate(-1);
                }
            } else {
                toast.error(data.message || "Ошибка при бронировании");
            }
        } catch {
            toast.error("Произошла ошибка. Попробуйте снова.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (reservationId) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Header restaurantName={restaurant?.name} />
                <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-5">
                        <CheckCircle2 size={40} className="text-green-500" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Столик забронирован!</h2>
                    <p className="text-sm text-gray-500 mb-1">
                        {date ? new Date(date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) : date} в {time}
                    </p>
                    <p className="text-sm text-gray-500 mb-8">{peopleCount} {peopleCount === 1 ? 'гость' : peopleCount < 5 ? 'гостя' : 'гостей'}</p>

                    <div className="w-full max-w-sm space-y-3">
                        <button
                            onClick={() => navigate('/reservation-preorder', { state: { reservationId, date, time } })}
                            className="w-full flex items-center justify-between bg-brandBlue text-white rounded-2xl px-5 py-4 font-semibold text-sm shadow-md active:scale-[0.98] transition-transform"
                        >
                            <span>Выбрать блюда заранее</span>
                            <ChevronRight size={18} />
                        </button>
                        <button
                            onClick={() => navigate(-1)}
                            className="w-full bg-white border border-gray-200 text-gray-700 rounded-2xl px-5 py-4 font-medium text-sm active:scale-[0.98] transition-transform"
                        >
                            Готово
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header restaurantName={restaurant?.name} />

            <div className="max-w-screen-md mx-auto px-4 pb-32 pt-5 page-enter">
                {/* Назад + заголовок */}
                <div className="flex items-center gap-3 mb-5">
                    <BackButton onClick={() => navigate(-1)} disabled={isSubmitting} />
                    <h1 className="text-xl font-bold text-gray-900">Бронирование столика</h1>
                </div>

                {/* Промо-плашка */}
                <div className="bg-brandBlue/8 border border-brandBlue/20 rounded-2xl px-4 py-3.5 mb-5 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-brandBlue/15 flex items-center justify-center shrink-0">
                        <CalendarDays size={18} className="text-brandBlue" />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-gray-900">Бесплатное бронирование</p>
                        <p className="text-xs text-brandBlue mt-0.5 font-medium">Без предоплаты · Отмена в любое время</p>
                    </div>
                </div>

                {/* Дата */}
                <SectionCard icon={CalendarDays} title="Дата визита">
                    <input
                        type="date"
                        value={date}
                        min={today}
                        onChange={(e) => setDate(e.target.value)}
                        disabled={isSubmitting}
                        className={fieldClass}
                    />
                </SectionCard>

                {/* Время */}
                <SectionCard icon={Clock} title="Время">
                    <p className="text-xs text-gray-400 -mt-2 mb-3">
                        Ресторан работает: {workingHoursStart} – {workingHoursEnd}
                    </p>
                    <input
                        type="time"
                        value={time}
                        min={minTime}
                        max={workingHoursEnd}
                        onChange={(e) => setTime(e.target.value)}
                        disabled={isSubmitting}
                        className={fieldClass}
                    />
                </SectionCard>

                {/* Количество персон */}
                <SectionCard icon={Users} title="Количество гостей">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Сколько вас будет?</span>
                        <div className="flex items-center gap-3 bg-gray-50 rounded-2xl border border-gray-200 p-1">
                            <button
                                onClick={() => setPeopleCount(p => Math.max(1, p - 1))}
                                disabled={isSubmitting || peopleCount <= 1}
                                className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-500 hover:bg-white hover:shadow-sm transition-all disabled:opacity-30"
                            >
                                <Minus size={14} strokeWidth={2.5} />
                            </button>
                            <span className="text-base font-bold text-gray-900 min-w-[28px] text-center">{peopleCount}</span>
                            <button
                                onClick={() => setPeopleCount(p => Math.min(20, p + 1))}
                                disabled={isSubmitting || peopleCount >= 20}
                                className="w-9 h-9 flex items-center justify-center rounded-xl bg-brandBlue text-white hover:bg-brandBlue/90 transition-colors disabled:opacity-30"
                            >
                                <Plus size={14} strokeWidth={2.5} />
                            </button>
                        </div>
                    </div>
                </SectionCard>

                {/* Комментарий */}
                <SectionCard icon={MessageSquare} title="Пожелания">
                    <textarea
                        rows={3}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        disabled={isSubmitting}
                        placeholder="Например: столик у окна, детский стульчик, повод для встречи…"
                        className={`${fieldClass} resize-none`}
                    />
                </SectionCard>
            </div>

            {/* Фиксированная кнопка */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-4 shadow-lg z-10">
                <div className="max-w-screen-md mx-auto">
                    <PrimaryButton
                        onClick={handleReservation}
                        disabled={isSubmitting}
                        className="w-full flex items-center justify-center gap-2 py-3.5"
                    >
                        {isSubmitting
                            ? <Loader className="animate-spin" size={18} />
                            : "Забронировать столик"
                        }
                    </PrimaryButton>
                </div>
            </div>
        </div>
    );
};

export default ReservationPage;
